import type { StatisticSectionRow } from './types';

export type SectionModes = Readonly<Record<string, string>>;

export const getSectionKey = (section: StatisticSectionRow): string =>
  section.key ?? section.parameter;

export const getActiveSectionMode = (
  section: StatisticSectionRow,
  modes: SectionModes,
): string | undefined => {
  const selected = modes[getSectionKey(section)] ?? section.defaultMode;
  return selected && section.childrenByMode?.[selected]
    ? selected
    : section.defaultMode;
};

export const getSectionRows = (
  section: StatisticSectionRow,
  modes: SectionModes,
): readonly StatisticSectionRow[] => {
  const mode = getActiveSectionMode(section, modes);
  return (mode && section.childrenByMode?.[mode]) || section.children || [];
};

export const buildNestedRows = (
  rows: readonly StatisticSectionRow[],
): readonly StatisticSectionRow[] => {
  if (!rows.some((row) => (row.level ?? 0) > 0)) return rows;

  const roots: StatisticSectionRow[] = [];
  const stack: StatisticSectionRow[] = [];
  rows.forEach((row) => {
    const level = Math.max(row.level ?? 0, 0);
    const children = row.children ? buildNestedRows(row.children) : undefined;
    const nested: StatisticSectionRow = children?.length
      ? { ...row, children: [...children] }
      : { ...row };
    const parent = level > 0 ? stack[level - 1] : undefined;
    if (parent) {
      parent.children = [...(parent.children ?? []), nested];
    } else {
      roots.push(nested);
    }
    stack[level] = nested;
    stack.length = level + 1;
  });
  return roots;
};
